<template>
  <main class="bureau-shell">
    <div class="bureau-ambience" aria-hidden="true"></div>

    <section v-if="accessState !== 'ready' && accessState !== 'anonymous'" class="gate-scene">
      <article class="register-sheet" aria-live="polite" :aria-busy="!accessBlocked">
        <p class="eyebrow">Bureau des mandataires</p>
        <h1>{{ accessState === 'elsewhere' ? 'Session ouverte ailleurs' : accessState === 'error' ? 'Le registre reste fermé' : 'Ouverture du registre' }}</h1>
        <p>{{ accessBlocked ? accessError : accessState === 'profile' ? 'Votre connexion est validée. Nous consultons votre statut de mandataire…' : 'Vérification de la connexion et de votre session…' }}</p>
        <div v-if="accessBlocked" class="settings-actions">
          <button class="seal-button" @click="retryAccess">{{ accessState === 'elsewhere' ? 'Ouvrir ici' : 'Réessayer' }}</button>
          <button class="ink-button" @click="logout">Utiliser un autre compte</button>
        </div>
      </article>
    </section>

    <section v-else-if="!user && section === 'legal'" class="gate-scene public-legal-scene">
      <article class="register-sheet legal-copy">
        <p class="eyebrow">Informations publiques</p>
        <h1>Mentions, confidentialité et conditions</h1>
        <h3>Identité et comptes liés</h3>
        <p>Google peut servir de fournisseur d’identité. La Guilde ne reçoit jamais votre mot de passe Google et conserve sa propre session de compte. Une identité n’est jamais fusionnée sur la seule base d’une adresse e-mail identique.</p>
        <h3>Essence et publications</h3>
        <p>Les quêtes et les nouveaux événements sont gratuits. Seuls les donjons sponsorisés consomment de l’Essence selon un devis serveur. Les anciens événements payés conservent leur contrat. L’Essence n’est pas convertible en argent.</p>
        <h3>Lyra</h3>
        <p>Lyra est une assistante automatisée. N’y saisissez aucun mot de passe, jeton, donnée bancaire ou information sensible. Les messages nécessaires à sa réponse peuvent être traités par le fournisseur d’IA configuré par la Guilde.</p>
        <h3>Version bêta</h3>
        <p>Les coordonnées définitives de l’éditeur et de l’hébergeur, les durées de conservation et les conditions commerciales complètes doivent être renseignées avant l’ouverture publique.</p>
        <button class="ink-button" @click="section = 'dashboard'">Retour à la connexion</button>
      </article>
    </section>

    <section v-else-if="!user" class="gate-scene">
      <div class="guild-sign">
        <span class="guild-seal">G</span>
        <div>
          <p class="eyebrow">La Guilde des Aventuriers</p>
          <h1>Bureau des mandataires</h1>
        </div>
      </div>
      <div class="register-sheet">
        <p>Créez votre compte directement ici, puis confiez une mission aux aventuriers sans télécharger le jeu.</p>
        <div class="auth-tabs" role="tablist" aria-label="Accès au Bureau">
          <button type="button" role="tab" :aria-selected="authMode === 'login'" :class="{ active: authMode === 'login' }" @click="authMode = 'login'">Se connecter</button>
          <button type="button" role="tab" :aria-selected="authMode === 'register'" :class="{ active: authMode === 'register' }" @click="authMode = 'register'">Créer un compte</button>
        </div>

        <section class="google-auth-panel" aria-labelledby="google-auth-title">
          <h2 id="google-auth-title">Connexion ou inscription Google</h2>
          <p>À votre première visite, Google crée votre compte Guilde avec votre adresse déjà vérifiée. La Guilde ne reçoit jamais votre mot de passe Google.</p>
          <div v-if="GOOGLE_CLIENT_ID" ref="googleButton" class="google-button" aria-label="Continuer avec Google"></div>
          <p v-else-if="authConfigState === 'loading'" class="google-config-note" role="status">Vérification de la connexion Google auprès du serveur…</p>
          <p v-else-if="authConfigState === 'error'" class="google-config-note">La configuration de connexion n’a pas pu être chargée. <button class="text-button compact" @click="loadAuthConfig">Réessayer</button></p>
          <p v-else class="google-config-note">La connexion Google n’est pas configurée sur ce serveur. La connexion par e-mail reste disponible.</p>
        </section>
        <div class="rule"><span>ou</span></div>

        <form v-if="authMode === 'login'" @submit.prevent="loginPassword" class="login-form">
          <label>Adresse de compte<input v-model.trim="login.email" type="email" autocomplete="email" required /></label>
          <label>Mot de passe<input v-model="login.password" type="password" autocomplete="current-password" required /></label>
          <button class="seal-button" :disabled="busy">{{ busy ? 'Ouverture…' : 'Entrer au bureau' }}</button>
          <button class="text-button compact" type="button" :disabled="busy" @click="requestPasswordReset">Mot de passe oublié ?</button>
        </form>

        <form v-else @submit.prevent="registerPassword" class="login-form">
          <label>Adresse e-mail<input v-model.trim="registration.email" type="email" autocomplete="email" required /></label>
          <label>Mot de passe<input v-model="registration.password" type="password" autocomplete="new-password" minlength="8" maxlength="128" required /><small>8 caractères minimum.</small></label>
          <label>Confirmer le mot de passe<input v-model="registration.confirmPassword" type="password" autocomplete="new-password" minlength="8" maxlength="128" required /></label>
          <label class="check-line registration-consent"><input v-model="registration.accepted" type="checkbox" required /> J’accepte les conditions d’utilisation et la politique de confidentialité du Bureau.</label>
          <div v-if="TURNSTILE_SITE_KEY" ref="turnstileSlot" class="turnstile-slot" aria-label="Contrôle anti-robot"></div>
          <button class="seal-button" :disabled="busy || !registration.accepted">{{ busy ? 'Inscription…' : 'Créer mon compte' }}</button>
        </form>

        <p v-if="notice" class="notice" :data-tone="noticeTone">{{ notice }}</p>
        <button v-if="verificationPending" class="ink-button resend-button" type="button" :disabled="busy" @click="resendVerification">Renvoyer l’e-mail de vérification</button>
        <button class="text-button" @click="section = 'legal'">Confidentialité et mentions</button>
      </div>
    </section>

    <template v-else>
      <header class="desk-header">
        <button class="brand-button" @click="section = 'dashboard'">
          <span class="guild-seal small">G</span>
          <span><small>Bureau de Guilde</small><strong>{{ profile?.displayName || user.email }}</strong></span>
        </button>
        <div class="essence-vial" title="Solde d’Essence"><span>✦</span>{{ essence }}</div>
        <button class="menu-toggle" @click="navOpen = !navOpen" aria-label="Ouvrir le registre">☰</button>
      </header>

      <nav class="leather-tabs" :class="{ open: navOpen }" aria-label="Registre du mandataire">
        <button v-for="item in navigation" :key="item.id" :class="{ active: section === item.id }" @click="openSection(item.id)">
          <span>{{ item.icon }}</span>{{ item.label }}
        </button>
      </nav>

      <section v-if="!profile || profile.status !== 'active'" class="register-sheet activation-sheet">
        <p class="eyebrow">Inscription au registre</p>
        <button class="text-button compact" @click="logout">Se déconnecter</button>
        <h2>{{ profile?.status === 'pending' ? 'Candidature en cours d’étude' : profile?.status === 'suspended' ? 'Accès mandataire suspendu' : 'Candidater comme mandataire' }}</h2>
        <div v-if="profile?.status === 'pending' || profile?.status === 'suspended'" class="pending-application">
          <p>{{ profile.status === 'pending' ? 'Votre demande a été transmise à l’Intendance. Les outils de publication resteront scellés jusqu’à la validation d’un administrateur.' : 'Votre accès est suspendu. Contactez l’Intendance avant toute nouvelle candidature.' }}</p>
          <small v-if="profile.status === 'pending'">Demande envoyée le {{ profile.submittedAt ? new Date(profile.submittedAt).toLocaleDateString('fr-FR') : '—' }}</small>
        </div>
        <template v-else>
        <p v-if="profile?.status === 'rejected'" class="notice" data-tone="error">{{ profile.reviewNote || 'L’Intendance demande des précisions avant une nouvelle étude.' }}</p>
        <label>Vous agissez comme
          <select v-model="activation.kind">
            <option value="individual">Particulier</option>
            <option value="business">Commerce ou entreprise</option>
            <option value="association">Association</option>
            <option value="public_body">Collectivité ou organisme public</option>
          </select>
        </label>
        <label>Nom présenté aux aventuriers<input v-model.trim="activation.displayName" maxlength="100" /></label>
        <label>Pourquoi souhaitez-vous devenir mandataire ?<textarea v-model.trim="activation.motivation" minlength="20" maxlength="1200" rows="6" placeholder="Décrivez les besoins que vous souhaitez publier et la manière dont vous assurerez leur bon déroulement."></textarea><small>{{ activation.motivation.length }}/1200 · 20 caractères minimum</small></label>
        <label class="check-line"><input v-model="activation.accepted" type="checkbox" /> J’accepte les conditions bêta du bureau et les règles de publication.</label>
        <button class="seal-button" :disabled="busy || !activation.accepted || activation.motivation.trim().length < 20" @click="activateProfile">Transmettre ma candidature</button>
        <p v-if="notice" class="notice" :data-tone="noticeTone">{{ notice }}</p>
        </template>
      </section>

      <div v-else class="desk-layout">
        <section v-if="section === 'dashboard'" class="ledger-page">
          <div class="page-heading"><p class="eyebrow">Registre du jour</p><h2>Votre comptoir d’intendance</h2></div>
          <div class="dashboard-ribbons">
            <article><span>Essence disponible</span><strong>✦ {{ essence }}</strong></article>
            <article><span>Statut</span><strong>Mandataire actif</strong></article>
            <article><span>Créations</span><strong>{{ creations.length }}</strong></article>
          </div>
          <div class="notice-board">
            <button @click="section = 'quest'"><b>📜 Afficher une quête</b><span>Gratuite, estimée par la Guilde</span></button>
            <button @click="section = 'manage-quests'"><b>🗂️ Gérer mes quêtes</b><span>Modifier, retirer ou valider un mandat</span></button>
            <button @click="section = 'event'"><b>🎪 Organiser un événement</b><span>Chasse gratuite et file facultative</span></button>
            <button @click="section = 'dungeon'"><b>🛡️ Ouvrir un donjon</b><span>Devis selon son contenu</span></button>
            <button @click="section = 'scanner'"><b>🔳 Scanner un Sceau</b><span>Valider le mandat remis</span></button>
          </div>
          <h3>Dernières créations</h3>
          <div v-if="!creations.length" class="empty-note">Aucune création n’est encore inscrite à votre nom.</div>
          <ul v-else class="creation-list"><li v-for="item in creations" :key="item.kind + item.id"><b>{{ creationIcon(item.kind) }} {{ item.title }}</b><span>{{ item.detail }}</span></li></ul>
        </section>

        <section v-if="section === 'manage-quests'" class="ledger-page">
          <div class="page-heading"><p class="eyebrow">Registre des mandats</p><h2>Gérer mes quêtes</h2></div>
          <p class="intro-copy">Toute modification du titre ou de la description relance la vérification de la Guilde et recalcule le rang ainsi que la prime. Une quête révisée devra être acceptée de nouveau par les aventuriers.</p>
          <div v-if="!mandateQuests.length" class="empty-note">Aucune quête dans votre registre.</div>
          <div v-else class="mandate-register">
            <article v-for="item in mandateQuests" :key="item.id" class="mandate-card">
              <header><div><span>Rang {{ item.rang || '?' }} · {{ item.recompense || 0 }} CVP</span><h3>{{ item.emoji || '📍' }} {{ item.title || item.titre }}</h3></div><small>révision {{ item.revision || 1 }}</small></header>
              <p>{{ item.description }}</p>
              <p v-if="item.status === 'completed'">Quête accomplie. Le registre des remises reste disponible.</p>
              <p v-else-if="item.status === 'expired'">Échéance passée. Les parcours terminés à temps restent à valider.</p>
              <QuestJourneyRegister v-if="item.journey || item.offeredReward" :quest="item" :request="request" />
              <div v-if="!['completed','expired'].includes(item.status)" class="mandate-actions">
                <button class="seal-button" type="button" @click="openQuestScanner(item)">Scanner un Sceau</button>
                <button class="ink-button" type="button" @click="startQuestEdit(item)">Modifier</button>
                <button class="danger-button" type="button" :disabled="busy" @click="removeQuest(item)">Supprimer</button>
              </div>
              <form v-if="editingQuestId === item.id" class="form-grid mandate-edit" @submit.prevent="saveQuestEdit">
                <label>Titre<input v-model.trim="questEdit.title" maxlength="100" required /></label>
                <label>Durée d’affichage
                  <select v-model.number="questEdit.days">
                    <option v-if="!QUEST_DURATION_OPTIONS.includes(questEdit.days)" :value="questEdit.days">{{ questEdit.days }} jours (durée actuelle)</option>
                    <option v-for="days in QUEST_DURATION_OPTIONS" :key="days" :value="days">{{ days }} jours</option>
                  </select>
                </label>
                <label class="wide">Description<textarea v-model.trim="questEdit.description" maxlength="1600" required></textarea></label>
                <fieldset class="wide choice-field">
                  <legend>Icône de la quête</legend>
                  <div class="emoji-picker">
                    <button
                      v-for="icon in QUEST_EMOJIS"
                      :key="icon"
                      type="button"
                      class="emoji-choice"
                      :class="{ active: questEdit.emoji === icon }"
                      :aria-label="`Choisir l’icône ${icon}`"
                      :aria-pressed="questEdit.emoji === icon"
                      @click="questEdit.emoji = icon"
                    >{{ icon }}</button>
                  </div>
                </fieldset>
                <fieldset class="wide choice-field">
                  <legend>Tags <span class="required-mark">obligatoire</span></legend>
                  <div class="tag-picker">
                    <button
                      v-for="tag in QUEST_TAGS"
                      :key="tag.id"
                      type="button"
                      class="tag-choice"
                      :class="{ active: questEdit.tags.includes(tag.id) }"
                      :style="questEdit.tags.includes(tag.id) ? { '--tag-color': tag.color } : {}"
                      :aria-pressed="questEdit.tags.includes(tag.id)"
                      @click="toggleQuestTag(questEdit.tags, tag.id)"
                    ><span aria-hidden="true">{{ tag.icon }}</span> {{ tag.label }}</button>
                  </div>
                </fieldset>
                <LocationPicker v-model:lat="questEdit.lat" v-model:lng="questEdit.lng" label="Lieu révisé de la quête" />
                <p class="wide revision-warning">Une modification du texte ou du lieu invalide les acceptations en cours : l’aventurier devra relire le mandat.</p>
                <QuestJourneyEditor v-model="questEdit.journey" :default-location="{ lat: questEdit.lat, lng: questEdit.lng }" class="wide" />
                <OfferedRewardEditor v-model="questEdit.offeredReward" class="wide" />
                <QuestPublicationRules v-model="questEdit.rulesAccepted" compact class="wide" />
                <div class="wide action-row"><button class="ink-button" type="button" @click="cancelQuestEdit">Annuler</button><button class="seal-button" :disabled="busy || !questEdit.rulesAccepted">Vérifier et enregistrer</button></div>
              </form>
            </article>
          </div>
        </section>

        <section v-if="section === 'scanner'" class="ledger-page scanner-page">
          <div class="page-heading"><p class="eyebrow">Remise en main propre</p><h2>Scanner la Carte de Guilde</h2></div>
          <p class="intro-copy">Visez le Sceau QR présenté par l’aventurier : il sélectionne automatiquement le bon mandat. Pour une Carte de Guilde, choisissez d’abord la quête concernée.</p>
          <label>Mandat à valider
            <select v-model="scannerQuestId">
              <option value="">Choisir une quête active</option>
              <option v-for="item in scannableQuests" :key="item.id" :value="item.id">{{ item.title || item.titre }} · rang {{ item.rang || '?' }}</option>
            </select>
          </label>
          <div class="scanner-frame" :class="{ active: scannerOn }">
            <video ref="scannerVideo" autoplay playsinline muted></video>
            <p v-if="!scannerOn">La caméra reste éteinte jusqu’à votre ordre.</p>
          </div>
          <p class="scanner-hint" aria-live="polite">{{ scannerHint }}</p>
          <div class="action-row scanner-actions">
            <button class="seal-button" :disabled="scannerBusy" @click="toggleScanner">{{ scannerOn ? 'Arrêter la caméra' : 'Ouvrir la caméra' }}</button>
          </div>
          <form class="manual-scan" @submit.prevent="validateManualCard">
            <label>Solution de secours
              <input v-model.trim="scannerManualText" type="text" inputmode="text" autocomplete="one-time-code" placeholder="Code à 6 chiffres (ex. 123 456) ou URL du Sceau" />
            </label>
            <button class="ink-button" :disabled="!scannerManualText || scannerBusy">Lire ce Sceau</button>
          </form>
          <div v-if="scannerResult" class="valuation-slip"><span>Sceau validé</span><strong>Mandat accompli · {{ scannerResult.reward }} CVP attribués</strong></div>
          <p class="privacy-hint">Le Sceau contient l’identifiant du mandat et un code temporaire à six chiffres. Le bureau ne reçoit ni e-mail, ni position, ni marque manuscrite du serment.</p>
        </section>

        <section v-if="section === 'quest'" class="ledger-page">
          <div class="page-heading"><p class="eyebrow">Avis d’entraide</p><h2>Créer une quête gratuite</h2></div>
          <form class="form-grid" @submit.prevent="publishQuest">
            <label>Titre<input v-model.trim="quest.title" maxlength="100" required /></label>
            <label>Durée d’affichage
              <select v-model.number="quest.days">
                <option v-for="days in QUEST_DURATION_OPTIONS" :key="days" :value="days">{{ days }} jours</option>
              </select>
            </label>
            <label class="wide">Description<textarea v-model.trim="quest.description" maxlength="1600" required></textarea></label>
            <div class="wide quest-rewrite-request">
              <button type="button" class="ink-button" :disabled="rewritingQuest || !quest.description" @click="rewriteQuestDraft">
                {{ rewritingQuest ? 'Lyra relit votre brouillon…' : '✦ Demander à Lyra de reformuler' }}
              </button>
              <span>Écrivez naturellement, même avec des fautes. Votre texte ne sera jamais remplacé sans votre accord.</span>
            </div>
            <QuestRewriteSuggestion
              v-if="questRewrite"
              :suggestion="questRewrite"
              formal
              class="wide"
              @apply="applyQuestRewrite"
              @dismiss="questRewrite = null"
            />
            <fieldset class="wide choice-field">
              <legend>Icône de la quête</legend>
              <div class="emoji-picker">
                <button
                  v-for="icon in QUEST_EMOJIS"
                  :key="icon"
                  type="button"
                  class="emoji-choice"
                  :class="{ active: quest.emoji === icon }"
                  :aria-label="`Choisir l’icône ${icon}`"
                  :aria-pressed="quest.emoji === icon"
                  @click="quest.emoji = icon"
                >{{ icon }}</button>
              </div>
            </fieldset>
            <fieldset class="wide choice-field">
              <legend>Tags <span class="required-mark">obligatoire</span></legend>
              <div class="tag-picker">
                <button
                  v-for="tag in QUEST_TAGS"
                  :key="tag.id"
                  type="button"
                  class="tag-choice"
                  :class="{ active: quest.tags.includes(tag.id) }"
                  :style="quest.tags.includes(tag.id) ? { '--tag-color': tag.color } : {}"
                  :aria-pressed="quest.tags.includes(tag.id)"
                  @click="toggleQuestTag(quest.tags, tag.id)"
                ><span aria-hidden="true">{{ tag.icon }}</span> {{ tag.label }}</button>
              </div>
            </fieldset>
            <LocationPicker v-model:lat="quest.lat" v-model:lng="quest.lng" label="Lieu de la quête" />
            <QuestJourneyEditor v-model="quest.journey" :default-location="{ lat: quest.lat, lng: quest.lng }" class="wide" />
                <OfferedRewardEditor v-model="quest.offeredReward" class="wide" />
                <QuestPublicationRules v-model="quest.rulesAccepted" class="wide" />
            <div class="wide action-row">
              <button type="button" class="ink-button" :disabled="busy" @click="estimateQuest">Estimer la valeur</button>
              <button class="seal-button" :disabled="busy || !quest.rulesAccepted">Publier gratuitement</button>
            </div>
          </form>
          <div v-if="questModerationHold" class="moderation-hold" :data-severity="questModerationHold.severity">
            <span>{{ questModerationHold.pendingModeration ? 'Vérification humaine requise' : 'Publication bloquée avant diffusion' }}</span>
            <strong>{{ questModerationHold.error }}</strong>
            <p>La quête n’est pas visible sur la carte. Son instantané est conservé dans le registre de modération.</p>
            <button type="button" class="ink-button" :disabled="busy || !questModerationHold.caseId || questModerationHold.appealed" @click="requestQuestReevaluation">
              {{ questModerationHold.appealed ? 'Réévaluation demandée' : 'Demander une réévaluation à l’administrateur' }}
            </button>
          </div>
          <div v-if="questEstimate" class="valuation-slip">
            <span>Estimation indicative</span><strong>{{ questEstimate.montantMin }}–{{ questEstimate.montantMax }} CVP · rang {{ questEstimate.rang }}</strong>
            <p>{{ (questEstimate.facteursExplicatifs || []).join(' · ') }}</p>
          </div>
        </section>

        <section v-if="section === 'event'" class="ledger-page">
          <div class="page-heading"><p class="eyebrow">Les rencontres de la Guilde</p><h2>Créer un événement gratuit</h2></div>
          <form class="form-grid" @submit.prevent="publishEvent">
            <label>Titre<input v-model.trim="event.title" maxlength="100" required /></label>
            <label>Rayon du lieu (m)<input v-model.number="event.radius" type="number" min="10" max="1000" /></label>
            <label class="wide">Description<textarea v-model.trim="event.description" maxlength="1200"></textarea></label>
            <LocationPicker v-model:lat="event.lat" v-model:lng="event.lng" label="Lieu de l’événement" />
            <EventExperienceEditor class="wide" v-model:selections="event.selections" v-model:duration-minutes="event.duration" v-model:queue-enabled="event.queueEnabled" v-model:offered-reward="event.offeredReward" />
            <button class="seal-button" :disabled="busy || !event.selections.length">Publier gratuitement</button>
          </form>
          <h3>Mes événements et leurs files</h3>
          <p v-if="!managedEvents.length">Aucun événement à gérer.</p>
          <EventReception v-for="entry in managedEvents" :key="entry.id" :event="entry" manager :request-fn="request" :channel="socket" />
        </section>

        <section v-if="section === 'dungeon'" class="ledger-page">
          <div class="page-heading"><p class="eyebrow">Commande sponsorisée</p><h2>Ouvrir un donjon</h2></div>
          <p class="intro-copy">Cette commande génère un grimoire jouable. Le prix dépendra des étages, rencontres, coffres, maîtres et dimensions réellement produits.</p>
          <form class="form-grid" @submit.prevent="publishDungeon">
            <label>Nom public<input v-model.trim="dungeon.name" maxlength="100" required /></label>
            <label>Nombre d’étages<input v-model.number="dungeon.floors" type="number" min="3" max="100" /></label>
            <LocationPicker v-model:lat="dungeon.lat" v-model:lng="dungeon.lng" label="Entrée du donjon" />
            <div class="wide action-row">
              <button type="button" class="ink-button" :disabled="busy" @click="previewDungeon">Générer le devis</button>
              <button class="seal-button" :disabled="busy || !dungeonQuote">Confirmer et publier</button>
            </div>
          </form>
          <div v-if="dungeonQuote" class="valuation-slip"><span>Devis serveur à confirmer</span><strong>✦ {{ dungeonQuote.pricing.essenceCost }} · {{ dungeonQuote.pricing.copperBudget }} CVP de récompenses</strong><p>{{ dungeonQuote.floorCount }} étage(s) · barème {{ dungeonQuote.pricing.pricingVersion }}</p></div>
          <div v-else-if="lastDungeonPricing" class="valuation-slip"><span>Dernier sceau publié</span><strong>✦ {{ lastDungeonPricing.essenceCost }} · {{ lastDungeonPricing.copperBudget }} CVP de récompenses</strong></div>
        </section>

        <section v-if="section === 'lyra'" class="ledger-page lyra-page">
          <div class="page-heading"><p class="eyebrow">Archiviste automatisée</p><h2>Demander à Lyra</h2></div>
          <p class="privacy-hint">Lyra répond aux questions sur le jeu, les quêtes acceptées, la Charte, la publication et tous les services du Bureau. Ne lui communiquez ni mot de passe, ni paiement, ni donnée sensible.</p>
          <div class="chat-scroll"><article v-for="(message, index) in chat" :key="index" :data-role="message.role"><b>{{ message.role === 'user' ? 'Vous' : 'Lyra' }}</b><p>{{ message.text }}</p></article></div>
          <form class="chat-form" @submit.prevent="askLyra"><textarea v-model.trim="lyraQuestion" maxlength="1200" placeholder="Comment publier une quête récurrente ?"></textarea><button class="seal-button" :disabled="busy || !lyraQuestion">Consulter Lyra</button></form>
        </section>

        <section v-if="section === 'settings'" class="ledger-page">
          <div class="page-heading"><p class="eyebrow">Dossier de compte</p><h2>Options et comptes liés</h2></div>
          <dl class="account-card"><dt>Compte Guilde</dt><dd>{{ user.email }}</dd><dt>Statut</dt><dd>{{ profile.status }}</dd><dt>Type</dt><dd>{{ profile.kind }}</dd></dl>
          <h3>Moyens de connexion</h3>
          <ul class="identity-list"><li v-for="identity in identities" :key="identity.provider + identity.created_at"><b>{{ providerLabel(identity.provider) }}</b><span>{{ identity.email || 'Lié' }}</span></li></ul>
          <div v-if="!identities.some(i => i.provider === 'google')" ref="googleLinkButton" class="google-button"></div>
          <button class="ink-button" disabled>Steam — prochainement</button>
          <div class="settings-actions"><button class="ink-button" @click="section = 'legal'">Mentions et confidentialité</button><button class="danger-button" @click="logout">Se déconnecter</button></div>
        </section>

        <section v-if="section === 'legal'" class="ledger-page legal-copy">
          <div class="page-heading"><p class="eyebrow">Informations publiques</p><h2>Mentions, confidentialité et conditions</h2></div>
          <h3>Identité et comptes liés</h3><p>Google peut servir de fournisseur d’identité. La Guilde ne reçoit jamais votre mot de passe Google et conserve sa propre session de compte.</p>
          <h3>Essence et publications</h3><p>Les quêtes et les nouveaux événements sont gratuits. Seuls les donjons sponsorisés consomment de l’Essence selon un devis serveur. Les anciens événements payés conservent leur contrat. L’Essence n’est pas convertible en argent.</p>
          <h3>Lyra</h3><p>Lyra est une assistante automatisée. Les messages nécessaires à sa réponse peuvent être traités par le fournisseur d’IA configuré par la Guilde.</p>
          <h3>Version bêta</h3><p>Les informations définitives de l’éditeur, de l’hébergeur, les durées de conservation et les conditions commerciales complètes doivent être renseignées avant l’ouverture publique.</p>
          <a class="ink-button link-button" :href="legalRegisterUrl" target="_blank" rel="noopener">Consulter le registre légal complet</a>
        </section>

        <p v-if="notice" class="notice floating-notice" :data-tone="noticeTone">{{ notice }}</p>
      </div>

      <Teleport to="body">
        <div
          v-if="publishedQuestSummary"
          class="publication-overlay"
          role="presentation"
          @click.self="finishQuestPublication"
        >
          <article
            ref="publicationDialog"
            class="publication-receipt"
            role="dialog"
            aria-modal="true"
            aria-labelledby="publication-title"
            tabindex="-1"
            @keydown.esc.prevent="finishQuestPublication"
          >
            <div class="publication-seal" aria-hidden="true">G</div>
            <p class="eyebrow">Avis inscrit au registre</p>
            <h2 id="publication-title">Quête publiée avec succès</h2>
            <p class="publication-intro">Voici l’avis que les aventuriers pourront consulter sur la carte.</p>

            <section class="publication-quest-sheet" aria-label="Résumé de la quête publiée">
              <div class="publication-heading">
                <div>
                  <span class="publication-status">Publication active</span>
                  <h3>{{ publishedQuestSummary.emoji || '📍' }} {{ publishedQuestSummary.titre || publishedQuestSummary.title }}</h3>
                </div>
                <strong class="publication-rank">Rang {{ publishedQuestSummary.rang || '?' }}</strong>
              </div>
              <p class="publication-description">{{ publishedQuestSummary.description || publishedQuestSummary.objectif }}</p>
              <dl class="publication-details">
                <div><dt>Prime fixée</dt><dd>{{ publishedQuestSummary.recompense || 0 }} CVP</dd></div>
                <div><dt>Publication</dt><dd>Gratuite</dd></div>
                <div><dt>Durée</dt><dd>{{ publishedQuestDuration }}</dd></div>
                <div><dt>Fin d’affichage</dt><dd>{{ formatPublishedExpiry(publishedQuestSummary.expiresAt) }}</dd></div>
                <div class="publication-location"><dt>Lieu choisi</dt><dd>{{ publishedQuestLocation }}</dd></div>
              </dl>
              <div v-if="publishedQuestSummary.tags?.length" class="publication-tags" aria-label="Catégories de la quête">
                <span v-for="tag in publishedQuestSummary.tags" :key="tag">{{ tagIcon(tag) }} {{ tagLabel(tag) }}</span>
              </div>
            </section>

            <button class="seal-button publication-finish" type="button" @click="finishQuestPublication">Voir mes créations</button>
          </article>
        </div>
      </Teleport>
    </template>
  </main>
</template>

<script setup lang="ts">
import { computed, inject, nextTick, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import jsQR from 'jsqr';
import { BESTIAIRE } from '../domain/lore/bestiaire.js';
import { QUEST_TAGS, tagIcon, tagLabel } from '../domain/lore/questTags.js';
import { portalApi, makeIdempotencyKey, type PortalUser } from './api';
import { newActivationDraft, newQuestDraft, newEventDraft, newDungeonDraft, newQuestEditDraft, newChat } from './accountDrafts';
import { parseCardText } from '../ui/composables/useNfc';
import LocationPicker from './LocationPicker.vue';
import QuestJourneyEditor from '../ui/components/QuestJourneyEditor.vue';
import OfferedRewardEditor from '../ui/components/OfferedRewardEditor.vue';
import QuestJourneyRegister from '../ui/components/QuestJourneyRegister.vue';
import EventExperienceEditor from '../ui/components/EventExperienceEditor.vue';
import EventReception from '../ui/components/EventReception.vue';

import QuestPublicationRules from '../ui/components/QuestPublicationRules.vue';
import QuestRewriteSuggestion from '../ui/components/QuestRewriteSuggestion.vue';
import type { QuestRewrite } from '../ui/components/questRewrite';
import questPublicationPolicy from '../../shared/quest-publication-policy.json';

type TurnstileApi = {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  reset?: (widgetId: string) => void;
  remove?: (widgetId: string) => void;
};

const TERMS_VERSION = 'mandatary-beta-2026-08-23';
const QUEST_PUBLICATION_RULES_VERSION = questPublicationPolicy.version;
const { bindPortalSession, clearSession, readSession, request, requestArgs, saveSession, socket, subscribeSessionChanges } = inject('mandatary-api', portalApi);
const GOOGLE_CLIENT_ID = ref('');
const TURNSTILE_SITE_KEY = ref('');
const compiledTurnstileSiteKey = inject('mandatary-turnstile-site-key', String(import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim());
const authConfigState = ref<'loading' | 'ready' | 'error'>('loading');
const accessState = ref<'checking' | 'anonymous' | 'profile' | 'ready' | 'error' | 'elsewhere'>('checking');
const accessBlocked = computed(() => accessState.value === 'error' || accessState.value === 'elsewhere');
const accessError = ref('');
let sessionRevision = 0;
// Le jeton ciblé survit aux coupures, même avant la première connexion.
let activeSessionToken = readSession().token;
let connectionSuspended = false;
let ignoreStoredSession = false;
let accountDataUserId: string | null = null;
let unsubscribeSessionChanges: (() => void) | undefined;
let configRevision = 0;
let googleRenderRevision = 0;
const GAME_PUBLIC_URL = String(import.meta.env.VITE_GAME_PUBLIC_URL || '').replace(/\/$/, '');
const legalRegisterUrl = `${GAME_PUBLIC_URL}/mentions-legales`;
const QUEST_DURATION_OPTIONS = [7, 30, 90];
const QUEST_EMOJIS = ['📍','⚔️','🛡️','🏹','🗡️','🧙','🧝','🧛','🐉','🐺','🦇','🕸️','💀','👻','🎃','🌙','⭐','🔥','❄️','⚡','🌊','🍃','🪨','🌲','🍄','🍺','🍖','🎒','🗝️','🧭','🧪','🪙','💰','💎','👑','🎁','🏆','🎪','🎭','🎯','🎵','🎲','🚩'];
// Le cache n'est qu'une preuve à présenter au serveur, jamais une identité validée.
const user = ref<PortalUser | null>(null);
const profile = ref<any>(null);
const identities = ref<any[]>([]);
const essence = ref(0);
const eventMaxMonsterLevel = ref(20);
const dungeons = ref<any[]>([]);
const mandateQuests = ref<any[]>([]);
const mandateEvents = ref<any[]>([]);
const busy = ref(false);
const notice = ref('');
const noticeTone = ref('info');
const section = ref('dashboard');
const authMode = ref<'login' | 'register'>('login');
const navOpen = ref(false);
const googleButton = ref<HTMLElement | null>(null);
const googleLinkButton = ref<HTMLElement | null>(null);
const turnstileSlot = ref<HTMLElement | null>(null);
const login = reactive({ email: '', password: '' });
const registration = reactive({ email: '', password: '', confirmPassword: '', accepted: false });
const verificationPending = ref(false);
const turnstileToken = ref('');
let turnstileWidgetId: string | null = null;
let turnstileScript: Promise<TurnstileApi> | null = null;
const activation = reactive(newActivationDraft());
const quest = reactive(newQuestDraft());
const event = reactive(newEventDraft());
const dungeon = reactive(newDungeonDraft());
const questEstimate = ref<any>(null);
const questModerationHold = ref<any>(null);
const rewritingQuest = ref(false);
const questRewrite = ref<QuestRewrite | null>(null);
let questRewriteFingerprint = '';
let questRewriteRequestId = 0;
const managedEvents = ref<any[]>([]);
watch(section, value => { if (value === 'event' && user.value) void refreshPrivateData(); });
const lastDungeonPricing = ref<any>(null);
const dungeonQuote = ref<any>(null);
const scannerQuestId = ref('');
const scannerVideo = ref<HTMLVideoElement | null>(null);
const scannerOn = ref(false);
const scannerBusy = ref(false);
const scannerResult = ref<any>(null);
const scannerHint = ref('Cadrez le QR dans la fenêtre, à environ 15–30 cm de la caméra.');
const scannerManualText = ref('');
const editingQuestId = ref('');
const questEdit = reactive(newQuestEditDraft());
const publishedQuestSummary = ref<any>(null);
const publicationDialog = ref<HTMLElement | null>(null);
let scannerStream: MediaStream | null = null;
let scannerFrame: number | null = null;
let scannerDetector: any = null;
let scannerDecodeInFlight = false;
let scannerLastScanAt = 0;
const mutationKeys = reactive({ quest: '', event: '', dungeon: '' });
const lyraQuestion = ref('');
const chat = ref(newChat());
const creations = computed(() => [
  ...mandateQuests.value.map((item) => ({ ...item, detail: item.status === 'completed' ? 'Terminée' : `Quête · rang ${item.rang || '?'}` })),
  ...mandateEvents.value.map((item) => ({ ...item, detail: `Événement · ${item.remainingCopperBudget || 0} CVP restants` })),
  ...dungeons.value.map((item) => ({ ...item, kind: 'dungeon', title: item.name, detail: `Donjon · ${item.floorCount} étage(s)` })),
]);
const scannableQuests = computed(() => mandateQuests.value.filter((item: any) => !['completed','expired'].includes(item.status)));
const publishedQuestDuration = computed(() => {
  const milliseconds = Number(publishedQuestSummary.value?.dureeActiveMs);
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return 'Durée réglementaire';
  const days = Math.max(1, Math.round(milliseconds / 86400000));
  return `${days} jour${days > 1 ? 's' : ''}`;
});
const publishedQuestLocation = computed(() => {
  const coordinates = publishedQuestSummary.value?.coordonnees;
  const lat = Number(coordinates?.lat);
  const lng = Number(coordinates?.lng);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return publishedQuestSummary.value?.lieu || 'Lieu indiqué sur la carte';
  return `${publishedQuestSummary.value?.lieu || 'Lieu indiqué sur la carte'} · ${lat.toFixed(5)}, ${lng.toFixed(5)}`;
});
const navigation = [
  { id: 'dashboard', icon: '⌂', label: 'Comptoir' }, { id: 'quest', icon: '📜', label: 'Quête' },
  { id: 'manage-quests', icon: '🗂️', label: 'Mes quêtes' },
  { id: 'event', icon: '🎪', label: 'Événement' }, { id: 'dungeon', icon: '🛡️', label: 'Donjon' },
  { id: 'scanner', icon: '🔳', label: 'Scanner' }, { id: 'lyra', icon: '✦', label: 'Lyra' }, { id: 'settings', icon: '⚙', label: 'Options' },
];


watch(
  () => [dungeon.name, dungeon.floors, dungeon.lat, dungeon.lng],
  () => { dungeonQuote.value = null; },
);
watch(() => questDraftFingerprint(), (fingerprint) => {
  if (!questRewriteFingerprint || fingerprint === questRewriteFingerprint) return;
  questRewrite.value = null;
  questRewriteFingerprint = '';
  if (rewritingQuest.value) {
    questRewriteRequestId += 1;
    rewritingQuest.value = false;
  }
});
watch(() => [
  quest.title,
  quest.description,
  quest.tags.join('|'),
  quest.emoji,
  quest.days,
  quest.lat,
  quest.lng,
].join('\n'), (fingerprint, previous) => {
  if (previous !== undefined && fingerprint !== previous && quest.rulesAccepted) quest.rulesAccepted = false;
});
watch(() => [
  questEdit.title,
  questEdit.description,
  questEdit.tags.join('|'),
  questEdit.emoji,
  questEdit.days,
  questEdit.recurring,
  questEdit.lat,
  questEdit.lng,
].join('\n'), (fingerprint, previous) => {
  if (previous !== undefined && fingerprint !== previous && questEdit.rulesAccepted) questEdit.rulesAccepted = false;
});
watch(authMode, async (mode) => {
  if (mode === 'register') {
    await nextTick();
    await renderTurnstile();
  } else {
    clearTurnstile();
  }
});
watch(section, async (current) => {
  // Les mentions publiques démontent l'écran d'accès. Au retour, les widgets
  // tiers doivent être rendus dans leurs nouveaux conteneurs DOM.
  if (user.value) return;
  if (current !== 'dashboard') {
    clearTurnstile();
    return;
  }
  await nextTick();
  if (authMode.value === 'register') void renderTurnstile();
});
watch([googleButton, googleLinkButton, GOOGLE_CLIENT_ID], () => {
  if (googleButton.value) void renderGoogle(googleButton.value, 'login');
  else if (googleLinkButton.value) void renderGoogle(googleLinkButton.value, 'link');
}, { flush: 'post' });
watch([turnstileSlot, TURNSTILE_SITE_KEY], () => { void renderTurnstile(); }, { flush: 'post' });
watch(publishedQuestSummary, async (summary) => {
  if (!summary) return;
  await nextTick();
  publicationDialog.value?.focus();
});

function flash(message: string, tone = 'info') { notice.value = message; noticeTone.value = tone; window.setTimeout(() => { if (notice.value === message) notice.value = ''; }, 7000); }
function toggleQuestTag(tags: string[], tagId: string) {
  const index = tags.indexOf(tagId);
  if (index >= 0) tags.splice(index, 1);
  else tags.push(tagId);
}
function mutationKey(kind: keyof typeof mutationKeys) {
  if (!mutationKeys[kind]) mutationKeys[kind] = makeIdempotencyKey(`portal-${kind}`);
  return mutationKeys[kind];
}
function openSection(id: string) { if (id !== 'scanner') stopScanner(); section.value = id; navOpen.value = false; if (id === 'settings') void loadLinkedAccounts(); }
function providerLabel(provider: string) { return ({ password: 'E-mail et mot de passe', google: 'Google', steam: 'Steam' } as any)[provider] || provider; }
function creationIcon(kind: string) { return ({ quest: '📜', event: '🎪', dungeon: '🛡️' } as Record<string, string>)[kind] || '•'; }
function formatPublishedExpiry(value: unknown) {
  const timestamp = Number(value);
  if (!Number.isFinite(timestamp)) return 'Selon la durée choisie';
  return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long', timeStyle: 'short' }).format(new Date(timestamp));
}
function finishQuestPublication() {
  publishedQuestSummary.value = null;
  openSection('manage-quests');
}

async function loadTurnstile() {
  const existingApi = (window as typeof window & { turnstile?: TurnstileApi }).turnstile;
  if (existingApi) return existingApi;
  if (turnstileScript) return turnstileScript;
  turnstileScript = new Promise<TurnstileApi>((resolve, reject) => {
    const ready = () => {
      const api = (window as typeof window & { turnstile?: TurnstileApi }).turnstile;
      if (api) resolve(api);
      else reject(new Error('Contrôle anti-robot indisponible.'));
    };
    const existing = document.getElementById('cloudflare-turnstile-mandatary');
    if (existing) {
      existing.addEventListener('load', ready, { once: true });
      existing.addEventListener('error', () => reject(new Error('Contrôle anti-robot indisponible.')), { once: true });
      ready();
      return;
    }
    const script = document.createElement('script');
    script.id = 'cloudflare-turnstile-mandatary';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = ready;
    script.onerror = () => reject(new Error('Contrôle anti-robot indisponible.'));
    document.head.appendChild(script);
  });
  return turnstileScript;
}

function clearTurnstile() {
  turnstileToken.value = '';
  const api = (window as typeof window & { turnstile?: TurnstileApi }).turnstile;
  if (turnstileWidgetId && api?.remove) api.remove(turnstileWidgetId);
  turnstileWidgetId = null;
}

async function renderTurnstile() {
  if (!TURNSTILE_SITE_KEY.value || !turnstileSlot.value || turnstileWidgetId) return;
  try {
    const api = await loadTurnstile();
    if (!turnstileSlot.value || turnstileWidgetId || authMode.value !== 'register') return;
    turnstileWidgetId = api.render(turnstileSlot.value, {
      sitekey: TURNSTILE_SITE_KEY.value,
      theme: 'light',
      language: 'fr',
      callback: (token: string) => { turnstileToken.value = token; },
      'expired-callback': () => { turnstileToken.value = ''; },
      'error-callback': () => { turnstileToken.value = ''; },
    });
  } catch (error: any) {
    flash(error?.message || 'Le contrôle anti-robot ne peut pas se charger.', 'error');
  }
}

function resetTurnstile() {
  turnstileToken.value = '';
  const api = (window as typeof window & { turnstile?: TurnstileApi }).turnstile;
  if (turnstileWidgetId && api?.reset) api.reset(turnstileWidgetId);
}

async function loadGoogle() {
  if ((window as any).google?.accounts?.id) return (window as any).google;
  await new Promise<void>((resolve, reject) => { const script = document.createElement('script'); script.src = 'https://accounts.google.com/gsi/client'; script.async = true; script.onload = () => resolve(); script.onerror = () => reject(); document.head.appendChild(script); });
  return (window as any).google;
}
async function renderGoogle(target: HTMLElement | null, mode: 'login' | 'link') {
  if (!target || !GOOGLE_CLIENT_ID.value) return;
  const renderRevision = ++googleRenderRevision;
  const revision = sessionRevision;
  try {
    const google = await loadGoogle();
    if (!target.isConnected || revision !== sessionRevision || renderRevision !== googleRenderRevision) return;
    const challenge: any = await request('getGoogleNonce');
    if (!target.isConnected || revision !== sessionRevision || renderRevision !== googleRenderRevision) return;
    if (!challenge?.nonce) return flash(challenge?.error || 'Impossible de préparer la connexion Google.', 'error');
    const nonce = String(challenge.nonce);
    target.replaceChildren();
    google.accounts.id.initialize({ client_id: GOOGLE_CLIENT_ID.value, nonce, auto_select: false, callback: async ({ credential }: any) => {
      if (!credential || busy.value || revision !== sessionRevision || renderRevision !== googleRenderRevision) return;
      busy.value = true;
      const res: any = await request(mode === 'login' ? 'loginWithGoogle' : 'linkGoogleAccount', { credential, nonce });
      if (revision !== sessionRevision) return;
      busy.value = false;
      if (res?.error) { flash(res.error, 'error'); void renderGoogle(target, mode); return; }
      if (mode === 'link') { identities.value = res.identities || identities.value; return flash('Compte Google lié.', 'success'); }
      await acceptLogin(res);
    } });
    const availableWidth = Math.floor(target.getBoundingClientRect().width || 300);
    google.accounts.id.renderButton(target, { type: 'standard', theme: 'outline', size: 'large', text: mode === 'login' ? 'continue_with' : 'signin_with', shape: 'rectangular', width: Math.max(220, Math.min(300, availableWidth)) });
  } catch {
    flash('La connexion Google ne se charge pas. Utilisez votre e-mail ou réessayez plus tard.', 'error');
  }
}

async function acceptLogin(res: any) {
  if (!res?.userId || !res?.sessionToken || !res?.email) return flash('Réponse de connexion incomplète.', 'error');
  sessionRevision += 1;
  const nextUser = { userId: res.userId, email: res.email };
  adoptUser(nextUser);
  saveSession(res.sessionToken, nextUser);
  activeSessionToken = res.sessionToken;
  ignoreStoredSession = false;
  clearTurnstile();
  login.password = '';
  registration.password = '';
  registration.confirmPassword = '';
  accessState.value = 'profile';
  verificationPending.value = false;
  activation.displayName = res?.data?.player?.name || res.email.split('@')[0];
  await refreshPrivateData();
}
async function loginPassword() {
  if (busy.value) return;
  busy.value = true;
  const revision = sessionRevision;
  const res: any = await requestArgs('login', [login.email, login.password]);
  if (revision !== sessionRevision) return;
  busy.value = false;
  if (res?.error) {
    verificationPending.value = Boolean(res.verificationPending);
    return flash(res.error, 'error');
  }
  await acceptLogin(res);
}
async function registerPassword() {
  if (busy.value) return;
  if (registration.password.length < 8) return flash('Choisissez un mot de passe d’au moins 8 caractères.', 'error');
  if (registration.password !== registration.confirmPassword) return flash('Les deux mots de passe ne correspondent pas.', 'error');
  if (!registration.accepted) return flash('Acceptez les conditions pour ouvrir le compte.', 'error');
  if (authConfigState.value !== 'ready') return flash('Attendez le chargement de la configuration de connexion.', 'error');
  if (TURNSTILE_SITE_KEY.value && !turnstileToken.value) return flash('Terminez le contrôle anti-robot avant de créer le compte.', 'error');
  busy.value = true;
  const revision = sessionRevision;
  const res: any = await requestArgs('register', [registration.email, registration.password, null, turnstileToken.value || null]);
  if (revision !== sessionRevision) return;
  busy.value = false;
  if (res?.error) {
    resetTurnstile();
    return flash(res.error, 'error');
  }
  if (res?.verificationRequired) {
    login.email = registration.email;
    verificationPending.value = true;
    authMode.value = 'login';
    return flash(res.message || 'Compte créé. Vérifiez votre e-mail avant de vous connecter.', 'success');
  }
  await acceptLogin(res);
}
async function resendVerification() {
  const revision = sessionRevision;
  const email = (login.email || registration.email).trim();
  if (!email || busy.value) return flash('Indiquez d’abord l’adresse du compte.', 'error');
  busy.value = true;
  const res: any = await requestArgs('resendEmailVerification', [email]);
  if (revision !== sessionRevision) return;
  busy.value = false;
  flash(res?.message || res?.error || 'La demande de vérification a été transmise.', res?.mailAvailable === false || res?.error ? 'error' : 'success');
}
async function requestPasswordReset() {
  const revision = sessionRevision;
  const email = login.email.trim();
  if (!email || busy.value) return flash('Indiquez votre adresse e-mail avant de demander un nouveau mot de passe.', 'error');
  busy.value = true;
  const res: any = await requestArgs('requestPasswordReset', [email]);
  if (revision !== sessionRevision) return;
  busy.value = false;
  flash(res?.message || res?.error || 'Si ce compte existe, un lien vient d’être envoyé.', res?.mailAvailable === false || res?.error ? 'error' : 'success');
}
async function activateProfile() {
  const revision = sessionRevision;
  busy.value = true;
  const res: any = await request('submitMandataryApplication', { ...activation, termsAccepted: activation.accepted, termsVersion: TERMS_VERSION, contactEmail: user.value?.email });
  if (revision !== sessionRevision) return;
  busy.value = false;
  if (res?.error) return flash(res.error, 'error');
  profile.value = res.profile;
  flash('Candidature transmise. Le bureau s’ouvrira après validation de l’Intendance.', 'success');
}
async function refreshPrivateData() {
  const revision = sessionRevision;
  const p: any = await request('getMandataryProfile');
  if (revision !== sessionRevision || !user.value) return;
  if (!p?.success) {
    accessState.value = 'error';
    accessError.value = p?.error || 'Votre statut de mandataire n’a pas pu être confirmé. Réessayez.';
    return;
  }
  profile.value = p.profile;
  if (p.profile?.displayName) activation.displayName = p.profile.displayName;
  if (p.profile?.kind) activation.kind = p.profile.kind;
  if (p.profile?.motivation) activation.motivation = p.profile.motivation;
  accessState.value = 'ready';
  if (profile.value?.status !== 'active') return;
  const [e, d, c]: any[] = await Promise.all([request('getEssence'), request('dungeon2:myCreations', { kind: 'sponsor' }), request('getMandataryCreations')]);
  if (revision !== sessionRevision || !user.value) return;
  if (!e?.error) {
    essence.value = e.balance || 0;
    eventMaxMonsterLevel.value = e.eventMaxMonsterLevel || 20;
  }
  if (!d?.error) dungeons.value = d.dungeons || [];
  if (!c?.error) {
    mandateQuests.value = c.quests || [];
    mandateEvents.value = c.events || [];
    managedEvents.value = (c.events || []).filter((event: any) => event.lifecycleVersion === 2);
  }
  await loadLinkedAccounts();
}
async function loadLinkedAccounts() {
  if (!user.value) return;
  const revision = sessionRevision;
  const res: any = await request('getLinkedAccounts');
  if (revision !== sessionRevision || !user.value) return;
  if (!res?.error) identities.value = res.identities || [];
}
async function logout() {
  const revision = ++sessionRevision;
  // Seul le jeton possédé par cet onglet peut être effacé. S'il a été
  // remplacé ailleurs, cette connexion reste anonyme sans le réimporter.
  ignoreStoredSession = true;
  connectionSuspended = false;
  clearSession(activeSessionToken);
  activeSessionToken = '';
  resetPrivateData();
  resetAccountData();
  accessState.value = 'checking';
  if (socket.connected) await request('logout');
  if (revision !== sessionRevision) return;
  // Une nouvelle socket empêche un ancien bind/login en vol de rattacher
  // le compte quitté après la déconnexion.
  socket.disconnect();
  socket.connect();
}

function resetPrivateData() {
  user.value = null;
  profile.value = null;
  identities.value = [];
  essence.value = 0;
  dungeons.value = [];
  mandateQuests.value = [];
  mandateEvents.value = [];
  publishedQuestSummary.value = null;
  busy.value = false;
  rewritingQuest.value = false;
  questRewriteRequestId += 1;
  scannerBusy.value = false;
  stopScanner();
  clearTurnstile();
}

function resetAccountData() {
  accountDataUserId = null;
  Object.assign(activation, newActivationDraft());
  Object.assign(quest, newQuestDraft());
  Object.assign(event, newEventDraft());
  Object.assign(dungeon, newDungeonDraft());
  Object.assign(questEdit, newQuestEditDraft());
  Object.assign(mutationKeys, { quest: '', event: '', dungeon: '' });
  questEstimate.value = null;
  questRewrite.value = null;
  questRewriteFingerprint = '';
  questRewriteRequestId += 1;
  rewritingQuest.value = false;
  managedEvents.value = [];
  dungeonQuote.value = null;
  lastDungeonPricing.value = null;
  eventMaxMonsterLevel.value = 20;
  editingQuestId.value = '';
  scannerQuestId.value = '';
  scannerResult.value = null;
  scannerManualText.value = '';
  scannerBusy.value = false;
  scannerHint.value = 'Cadrez le QR dans la fenêtre, à environ 15–30 cm de la caméra.';
  lyraQuestion.value = '';
  chat.value = newChat();
  Object.assign(login, { email: '', password: '' });
  Object.assign(registration, { email: '', password: '', confirmPassword: '', accepted: false });
  verificationPending.value = false;
  authMode.value = 'login';
  section.value = 'dashboard';
  navOpen.value = false;
  notice.value = '';
}

function adoptUser(nextUser: PortalUser) {
  // Seule l'identité confirmée par le serveur décide si les brouillons
  // appartiennent encore au compte ; jamais la fiche du stockage local.
  if (accountDataUserId !== nextUser.userId) resetAccountData();
  accountDataUserId = nextUser.userId;
  user.value = nextUser;
}

async function loadAuthConfig() {
  const revision = ++configRevision;
  authConfigState.value = 'loading';
  GOOGLE_CLIENT_ID.value = '';
  const config: any = await request('getAuthConfig');
  if (revision !== configRevision) return;
  if (!config?.success) { authConfigState.value = 'error'; return; }
  GOOGLE_CLIENT_ID.value = String(config.googleClientId || '');
  TURNSTILE_SITE_KEY.value = String(config.turnstileSiteKey || '').trim() || compiledTurnstileSiteKey;
  authConfigState.value = 'ready';
}

async function onConnected() {
  if (connectionSuspended) { socket.disconnect(); return; }
  const revision = ++sessionRevision;
  resetPrivateData();
  accessState.value = 'checking';
  accessError.value = '';
  void loadAuthConfig();
  const session = ignoreStoredSession ? { token: '', user: null } : readSession();
  const { token } = session;
  // Couvre aussi une reprise réseau avant livraison de l'événement storage.
  if (!ignoreStoredSession && token !== activeSessionToken) { pauseForSessionChange(); return; }
  if (!token) { resetAccountData(); accessState.value = 'anonymous'; return; }
  const bound: any = await bindPortalSession(session);
  if (revision !== sessionRevision) return;
  if (readSession().token !== token) {
    pauseForSessionChange();
    return;
  }
  if (!bound?.success || !bound.userId || !bound.email) {
    if (/session (expirée|invalide)|compte introuvable/i.test(bound?.error || '')) {
      if (!clearSession(token)) { pauseForSessionChange(); return; }
      activeSessionToken = '';
      resetAccountData();
      accessState.value = 'anonymous';
      flash('Votre session a expiré. Reconnectez-vous pour ouvrir le Bureau.', 'info');
    } else {
      accessState.value = 'error';
      accessError.value = bound?.error || 'Votre session n’a pas pu être vérifiée.';
    }
    return;
  }
  // L'identité vient de la réponse serveur, pas de la fiche locale.
  const nextUser = { userId: bound.userId, email: bound.email };
  adoptUser(nextUser);
  saveSession(token, nextUser);
  accessState.value = 'profile';
  await refreshPrivateData();
}

function onDisconnected() {
  sessionRevision += 1;
  configRevision += 1;
  resetPrivateData();
  GOOGLE_CLIENT_ID.value = '';
  if (connectionSuspended) return;
  accessState.value = 'error';
  accessError.value = 'La connexion a été interrompue. Votre session est conservée ; le Bureau se rouvrira après vérification du serveur.';
}

function pauseForSessionChange() {
  connectionSuspended = true;
  sessionRevision += 1;
  configRevision += 1;
  socket.disconnect();
  resetPrivateData();
  GOOGLE_CLIENT_ID.value = '';
  accessState.value = 'elsewhere';
  accessError.value = 'Votre session a changé ou a été ouverte dans une autre fenêtre. Cet onglet reste fermé. Ouvrir ici reprendra la session et pourra interrompre l’autre fenêtre.';
}

function onSessionStorageChanged() {
  if (ignoreStoredSession || readSession().token === activeSessionToken) return;
  pauseForSessionChange();
}

function onConnectionError(error: Error & { data?: { code?: string } }) {
  onDisconnected();
  if (connectionSuspended) return;
  accessError.value = error.data?.code === 'MISE_A_JOUR_REQUISE'
    ? 'Cette version du Bureau doit être mise à jour. Rechargez la page pour recevoir la dernière version.'
    : error.data?.code ? error.message : 'Impossible de joindre le serveur. Vérifiez votre connexion puis réessayez.';
}

function retryAccess() {
  if (connectionSuspended) {
    connectionSuspended = false;
    ignoreStoredSession = false;
    activeSessionToken = readSession().token;
  }
  if (socket.connected) void onConnected();
  else { accessState.value = 'checking'; socket.connect(); }
}

function questDraftFingerprint() {
  return `${quest.title}\n${quest.description}\n${quest.tags.join('|')}`;
}

async function rewriteQuestDraft() {
  const revision = sessionRevision;
  if (!quest.description.trim()) return flash('Décrivez d’abord le besoin avec vos propres mots.', 'info');
  const requestId = ++questRewriteRequestId;
  const requestedFingerprint = questDraftFingerprint();
  rewritingQuest.value = true;
  questRewrite.value = null;
  questRewriteFingerprint = requestedFingerprint;
  const res: any = await request('rewriteQuestDraft', {
    titre: quest.title,
    description: quest.description,
    tags: [...quest.tags],
  }, 45000);
  if (revision !== sessionRevision) return;
  if (requestId !== questRewriteRequestId) return;
  rewritingQuest.value = false;
  if (requestedFingerprint !== questDraftFingerprint()) return;
  if (res?.error || !res?.success || !res?.suggestion) return flash(res?.error || 'Lyra n’a pas pu relire ce brouillon.', 'error');
  questRewrite.value = res.suggestion;
}

function applyQuestRewrite() {
  const suggestion = questRewrite.value;
  if (!suggestion || suggestion.canApply === false) return;
  quest.title = suggestion.title;
  quest.description = suggestion.description;
  quest.rulesAccepted = false;
  questEstimate.value = null;
  questRewrite.value = null;
  questRewriteFingerprint = '';
  flash('Le brouillon de Lyra est appliqué. Relisez-le avant de publier.', 'success');
}

async function estimateQuest() {
  const revision = sessionRevision;
  if (!quest.tags.length) return flash('Choisissez au moins un tag pour estimer la quête.', 'error');
  busy.value = true;
  const res: any = await request('estimateQuest', { titre: quest.title, description: quest.description, tags: [...quest.tags] });
  if (revision !== sessionRevision) return;
  busy.value = false;
  if (res?.error) return flash(res.error, 'error');
  questEstimate.value = res.estimation;
}

async function validateQuestProof(proof: { questId?: string; cardId?: string; validationCode?: string; proofType: string; resolveQuestFromCode?: boolean }) {
  const revision = sessionRevision;
  const proofQuestId = proof.resolveQuestFromCode ? '' : String(proof.questId || scannerQuestId.value || '');
  if ((!proofQuestId && proof.validationCode === undefined) || scannerBusy.value) return;
  scannerBusy.value = true;
  stopScanner();
  const response: any = await request('validateQuestByMandataire', {
    questId: proofQuestId || undefined,
    ...proof,
  });
  if (revision !== sessionRevision) return;
  scannerBusy.value = false;
  if (response?.error || !response?.success) {
    scannerHint.value = response?.error || 'Le Sceau a été lu mais sa validation est refusée.';
    return flash(response?.error || 'Sceau invalide.', 'error');
  }
  if (response.questId) scannerQuestId.value = String(response.questId);
  scannerResult.value = response;
  scannerHint.value = 'Sceau validé.';
  await refreshPrivateData();
  if (revision !== sessionRevision) return;
  flash('Mandat validé et récompense attribuée.', 'success');
}

function validateScannedCard(cardId: string) {
  if (!scannerQuestId.value) {
    scannerHint.value = 'Choisissez d’abord le mandat associé à cette Carte de Guilde.';
    flash(scannerHint.value, 'error');
    return;
  }
  return validateQuestProof({ cardId, proofType: 'qr_mandataire_scan' });
}

function validateShortCode(code: string, questId?: string) {
  return validateQuestProof({
    questId,
    validationCode: code.replace(/\D/g, ''),
    proofType: 'short_code',
    resolveQuestFromCode: !questId,
  });
}

function openQuestScanner(item: any) {
  scannerQuestId.value = item.id;
  scannerResult.value = null;
  scannerManualText.value = '';
  scannerHint.value = `Mandat « ${item.title || item.titre} » sélectionné. Ouvrez la caméra quand l’aventurier présente son Sceau.`;
  section.value = 'scanner';
}

function startQuestEdit(item: any) {
  editingQuestId.value = item.id;
  questEdit.title = item.title || item.titre || '';
  questEdit.description = item.description || item.objectif || '';
  questEdit.tags = Array.isArray(item.tags) ? [...item.tags] : [];
  questEdit.days = item.expiresAt ? Math.max(1, Math.min(90, Math.ceil((item.expiresAt - Date.now()) / 86400000))) : 30;
  questEdit.lat = item.coordonnees?.lat;
  questEdit.lng = item.coordonnees?.lng;
  questEdit.journey = item.journey ? JSON.parse(JSON.stringify(item.journey)) : null;
  questEdit.offeredReward = item.offeredReward ? { ...item.offeredReward } : null;
  questEdit.emoji = item.emoji || '📍';
  questEdit.recurring = item.recurring === true;
  questEdit.rulesAccepted = false;
}

function cancelQuestEdit() { editingQuestId.value = ''; }

async function saveQuestEdit() {
  const revision = sessionRevision;
  if (!editingQuestId.value || busy.value) return;
  if (!questEdit.tags.length) return flash('Choisissez au moins un tag.', 'error');
  if (questEdit.lat == null || questEdit.lng == null) return flash('Indiquez le lieu de la quête.', 'error');
  if (!questEdit.rulesAccepted) return flash('Confirmez à nouveau la Charte avant de publier cette révision.', 'error');
  busy.value = true;
  const response: any = await request('updateQuest', {
    questId: editingQuestId.value,
    journey: questEdit.journey, offeredReward: questEdit.offeredReward,
    titre: questEdit.title,
    description: questEdit.description,
    tags: [...questEdit.tags],
    emoji: questEdit.emoji,
    recurring: questEdit.recurring,
    dureeActiveMs: Math.max(1, Number(questEdit.days) || 30) * 86400000,
    coordonnees: { lat: questEdit.lat, lng: questEdit.lng },
    lieu: 'Lieu indiqué par le mandataire',
    publicationRulesAccepted: true,
    publicationRulesVersion: QUEST_PUBLICATION_RULES_VERSION,
  });
  if (revision !== sessionRevision) return;
  busy.value = false;
  if (response?.pendingModeration || response?.rejectedByBot) {
    questModerationHold.value = { ...response, appealed: false };
    return flash(response.error || 'La révision attend une décision de la modération.', response.pendingModeration ? 'info' : 'error');
  }
  if (response?.error || !response?.success) return flash(response?.error || 'Modification refusée.', 'error');
  editingQuestId.value = '';
  await refreshPrivateData();
  if (revision !== sessionRevision) return;
  flash(response.reverified ? 'Quête vérifiée, réestimée et mise à jour.' : 'Quête mise à jour.', 'success');
}

async function removeQuest(item: any) {
  const revision = sessionRevision;
  if (busy.value || !window.confirm(`Supprimer définitivement la quête « ${item.title || item.titre} » ?`)) return;
  busy.value = true;
  const response: any = await requestArgs('cancelQuest', [item.id]);
  if (revision !== sessionRevision) return;
  busy.value = false;
  if (response?.error || !response?.success) return flash(response?.error || 'Suppression refusée.', 'error');
  if (scannerQuestId.value === item.id) scannerQuestId.value = '';
  if (editingQuestId.value === item.id) editingQuestId.value = '';
  await refreshPrivateData();
  if (revision !== sessionRevision) return;
  flash('Quête retirée de la carte.', 'success');
}

function readScannedText(value: string) {
  const mandateSeal = String(value || '').trim().match(/^GUILD-MANDATE:([^:]{1,160}):(\d{6})$/i);
  if (mandateSeal) {
    const sealedQuest = scannableQuests.value.find((item: any) => item.id === mandateSeal[1]);
    if (!sealedQuest) {
      scannerHint.value = 'Ce QR ne correspond à aucun de vos mandats actifs.';
      return false;
    }
    scannerQuestId.value = sealedQuest.id;
    scannerHint.value = 'Code à six chiffres reconnu. Vérification auprès de la Guilde…';
    void validateShortCode(mandateSeal[2], sealedQuest.id);
    return true;
  }
  const card = parseCardText(value);
  if (!card?.accountId) {
    scannerHint.value = 'Un QR a été lu, mais ce n’est pas une Carte de Guilde reconnue.';
    return false;
  }
  if (!scannerQuestId.value) {
    scannerHint.value = 'Carte de Guilde reconnue. Choisissez maintenant le mandat à valider.';
    return false;
  }
  scannerHint.value = 'Sceau reconnu. Vérification auprès de la Guilde…';
  void validateScannedCard(card.accountId);
  return true;
}

function validateManualCard() {
  if (!scannerManualText.value) return;
  const shortCode = scannerManualText.value.replace(/\D/g, '');
  if (/^\d{6}$/.test(shortCode) && /^[\d\s-]+$/.test(scannerManualText.value)) {
    void validateShortCode(shortCode);
    return;
  }
  if (!readScannedText(scannerManualText.value)) flash('Ce texte ne contient pas un Sceau de Guilde valide.', 'error');
}

function cameraFailureMessage(error: any) {
  if (!window.isSecureContext) return 'La caméra exige une adresse HTTPS (ou localhost). Ouvrez le Bureau déployé en HTTPS.';
  if (!navigator.mediaDevices?.getUserMedia) return 'Ce navigateur ne fournit pas l’accès caméra.';
  if (error?.name === 'NotAllowedError' || error?.name === 'SecurityError') return 'Autorisation refusée. Autorisez la caméra pour ce site dans les réglages du navigateur, puis réessayez.';
  if (error?.name === 'NotFoundError') return 'Aucune caméra n’a été trouvée sur cet appareil.';
  if (error?.name === 'NotReadableError') return 'La caméra est déjà utilisée par une autre application.';
  return error?.message || 'permission refusée';
}

async function toggleScanner() {
  const revision = sessionRevision;
  if (scannerOn.value) return stopScanner();
  scannerResult.value = null;
  scannerHint.value = 'Ouverture de la caméra…';
  try {
    if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) throw new Error(cameraFailureMessage(null));
    const stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: 'environment' },
        width: { ideal: 1280 },
        height: { ideal: 720 },
      },
      audio: false,
    });
    if (revision !== sessionRevision) { stream.getTracks().forEach(track => track.stop()); return; }
    scannerStream = stream;
    const cameraTrack = scannerStream.getVideoTracks()[0];
    if (!cameraTrack) throw Object.assign(new Error('Aucun flux vidéo reçu.'), { name: 'NotFoundError' });
    if (cameraTrack) {
      const capabilities = typeof cameraTrack.getCapabilities === 'function' ? cameraTrack.getCapabilities() as any : null;
      if (Array.isArray(capabilities?.focusMode) && capabilities.focusMode.includes('continuous')) {
        await cameraTrack.applyConstraints({ advanced: [{ focusMode: 'continuous' } as any] }).catch(() => undefined);
      }
    }
    if (revision !== sessionRevision) return;
    scannerOn.value = true;
    await nextTick();
    if (revision !== sessionRevision) return;
    const video = scannerVideo.value;
    if (!video) throw new Error('Le lecteur vidéo de la caméra est indisponible.');
    video.srcObject = scannerStream;
    await video.play();
    if (revision !== sessionRevision) return;
    const BarcodeDetectorCtor = (window as typeof window & { BarcodeDetector?: any }).BarcodeDetector;
    scannerDetector = null;
    if (BarcodeDetectorCtor) {
      try { scannerDetector = new BarcodeDetectorCtor({ formats: ['qr_code'] }); }
      catch { scannerDetector = null; }
    }
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('Lecture vidéo indisponible');
    scannerHint.value = 'Recherche du Sceau… Gardez le QR net et entièrement visible.';
    const tick = async (timestamp: number) => {
      if (revision !== sessionRevision || !scannerOn.value || scannerBusy.value) return;
      if (!scannerDecodeInFlight && timestamp - scannerLastScanAt >= 120 && video.readyState >= 2 && video.videoWidth) {
        scannerDecodeInFlight = true;
        scannerLastScanAt = timestamp;
        let decodedText = '';
        if (scannerDetector) {
          try {
            const codes = await scannerDetector.detect(video);
            decodedText = String(codes?.find((code: any) => code?.rawValue)?.rawValue || '');
          } catch {
            scannerDetector = null;
          }
        }
        try {
          if (!decodedText && scannerOn.value) {
            // Une vidéo 4K passée brute à jsQR ralentit tellement la boucle que
            // le QR semble ne jamais être lu. On conserve au plus 960 px sur
            // le grand côté, largement assez pour le Sceau imprimé à l’écran.
            const scale = Math.min(1, 960 / Math.max(video.videoWidth, video.videoHeight));
            canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
            canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
            context.drawImage(video, 0, 0, canvas.width, canvas.height);
            const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
            decodedText = jsQR(pixels.data, pixels.width, pixels.height, { inversionAttempts: 'attemptBoth' })?.data || '';
          }
        } catch {
          // Une trame illisible est simplement ignorée ; la suivante arrive.
        } finally {
          scannerDecodeInFlight = false;
        }
        if (revision !== sessionRevision || !scannerOn.value || scannerBusy.value) return;
        if (decodedText && readScannedText(decodedText)) return;
      }
      scannerFrame = requestAnimationFrame(tick);
    };
    scannerFrame = requestAnimationFrame(tick);
  } catch (error: any) {
    if (revision !== sessionRevision) return;
    stopScanner();
    const message = cameraFailureMessage(error);
    scannerHint.value = message;
    flash(`Caméra inaccessible : ${message}`, 'error');
  }
}

function stopScanner() {
  scannerOn.value = false;
  if (scannerFrame !== null) cancelAnimationFrame(scannerFrame);
  scannerFrame = null;
  scannerStream?.getTracks().forEach((track) => track.stop());
  scannerStream = null;
  scannerDetector = null;
  scannerDecodeInFlight = false;
  if (!scannerBusy.value && !scannerResult.value) scannerHint.value = 'Caméra arrêtée. Vous pouvez la rouvrir ou saisir le code à six chiffres.';
}
async function publishQuest() {
  const revision = sessionRevision;
  if (busy.value) return;
  if (!quest.tags.length) return flash('Choisissez au moins un tag.', 'error');
  if (quest.lat == null || quest.lng == null) return flash('Indiquez le lieu de la quête.', 'error');
  if (!quest.rulesAccepted) return flash('Confirme la Charte de publication avant d’envoyer la quête.', 'error');
  busy.value = true;
  const res: any = await request('proposeQuest', { idempotencyKey: mutationKey('quest'), journey: quest.journey, offeredReward: quest.offeredReward, titre: quest.title, description: quest.description, tags: [...quest.tags], emoji: quest.emoji, coordonnees: { lat: quest.lat, lng: quest.lng }, dureeActiveMs: quest.days * 86400000, lieu: 'Lieu indiqué par le mandataire', publicationRulesAccepted: true, publicationRulesVersion: QUEST_PUBLICATION_RULES_VERSION });
  if (revision !== sessionRevision) return;
  busy.value = false;
  if (res?.pendingModeration || res?.rejectedByBot) {
    questModerationHold.value = { ...res, appealed: false };
    mutationKeys.quest = '';
    return flash(res.error || 'La quête attend une décision de la modération.', res.pendingModeration ? 'info' : 'error');
  }
  if (res?.error || !res?.success || !res?.quest) return flash(res?.error || 'La publication n’a pas été confirmée par la Guilde.', 'error');
  questModerationHold.value = null;
  const publishedQuest = { ...res.quest };
  mutationKeys.quest = '';
  questEstimate.value = null;
  quest.title = '';
  quest.description = '';
  quest.tags = ['aide'];
  quest.emoji = '📍';
  quest.days = 30;
  quest.lat = undefined;
  quest.lng = undefined;
  quest.rulesAccepted = false;
  quest.journey = null;
  quest.offeredReward = null;
  questRewrite.value = null;
  questRewriteFingerprint = '';
  questRewriteRequestId += 1;
  rewritingQuest.value = false;
  // Le reçu et la liste locale sont disponibles immédiatement, même si le
  // rafraîchissement du registre prend quelques secondes sur une connexion lente.
  mandateQuests.value = [publishedQuest, ...mandateQuests.value.filter((item: any) => item.id !== publishedQuest.id)];
  publishedQuestSummary.value = publishedQuest;
  await refreshPrivateData();
  if (revision !== sessionRevision) return;
}
async function requestQuestReevaluation() {
  if (!questModerationHold.value?.caseId || busy.value) return;
  const message = window.prompt('Ajoutez les précisions utiles à l’administrateur. La quête originale reste conservée telle quelle.');
  if (message === null) return;
  busy.value = true;
  const res: any = await request('moderation:requestReevaluation', { caseId: questModerationHold.value.caseId, message: message.trim() });
  busy.value = false;
  if (!res?.success) return flash(res?.error || 'La demande de réévaluation n’a pas pu être transmise.', 'error');
  questModerationHold.value.appealed = true;
  flash('Demande de réévaluation transmise à l’Intendance.', 'success');
}

async function publishEvent() {
  if (busy.value) return;
  if (!event.title.trim() || event.lat == null || event.lng == null || !event.selections.length) return flash('Indiquez le titre, le lieu et les espèces de la chasse.', 'error');
  const revision = sessionRevision;
  busy.value = true;
  const res: any = await request('createEvent', { idempotencyKey: mutationKey('event'), titre: event.title, description: event.description, lat: event.lat, lng: event.lng, durationMinutes: event.duration, radius: event.radius, selections: event.selections, queueEnabled: event.queueEnabled, offeredReward: event.offeredReward });
  if (revision !== sessionRevision) return;
  busy.value = false;
  if (!res?.success) return flash(res?.error || 'Publication impossible.', 'error');
  mutationKeys.event = '';
  Object.assign(event, newEventDraft());
  await refreshPrivateData();
  flash('Événement publié gratuitement. Le registre des passages est disponible.', 'success');
}

async function publishDungeon() {
  const revision = sessionRevision;
  if (busy.value) return;
  if (dungeon.lat == null || dungeon.lng == null) return flash('Indiquez l’entrée du donjon.', 'error');
  if (!dungeonQuote.value) return flash('Générez et vérifiez le devis avant de publier.', 'error');
  busy.value = true;
  const res: any = await request('dungeon2:createSponsor', { sponsorName: dungeon.name, floorCount: dungeon.floors, lat: dungeon.lat, lng: dungeon.lng, quoteId: dungeonQuote.value.quoteId, expectedEssenceCost: dungeonQuote.value.pricing.essenceCost, expectedPricingVersion: dungeonQuote.value.pricing.pricingVersion, idempotencyKey: mutationKey('dungeon') }, 16 * 60_000);
  if (revision !== sessionRevision) return;
  busy.value = false;
  if (res?.error) {
    if (res.quoteChanged && res.pricing) dungeonQuote.value = { ...dungeonQuote.value, quoteId: res.quoteId || dungeonQuote.value.quoteId, pricing: res.pricing };
    return flash(res.error, 'error');
  }
  mutationKeys.dungeon = ''; dungeonQuote.value = null; essence.value = res.essenceBalance; lastDungeonPricing.value = res.pricing; dungeon.name = ''; await refreshPrivateData();
  if (revision !== sessionRevision) return;
  flash(`Donjon publié : ✦ ${res.pricing?.essenceCost}.`, 'success');
}

async function previewDungeon() {
  const revision = sessionRevision;
  if (busy.value) return;
  if (dungeon.lat == null || dungeon.lng == null) return flash('Indiquez l’entrée du donjon.', 'error');
  busy.value = true;
  const res: any = await request('dungeon2:previewSimpleSponsor', { sponsorName: dungeon.name, floorCount: dungeon.floors, lat: dungeon.lat, lng: dungeon.lng }, 16 * 60_000);
  if (revision !== sessionRevision) return;
  busy.value = false;
  if (res?.error) return flash(res.error, 'error');
  dungeonQuote.value = res;
  essence.value = res.essenceBalance;
  flash('Devis du donjon prêt. Vérifiez le coût avant de confirmer.', 'success');
}
async function askLyra() {
  const revision = sessionRevision;
  const text = lyraQuestion.value; if (!text) return;
  chat.value.push({ role: 'user', text }); lyraQuestion.value = ''; busy.value = true;
  const res: any = await request('askLyra', { text, player: { name: profile.value?.displayName || 'Mandataire' }, wallet: { essence: essence.value }, inventory: {}, party: {}, codexProgress: {} }, 45000);
  if (revision !== sessionRevision) return;
  busy.value = false;
  chat.value.push({ role: 'lyra', text: res?.text || res?.error || 'Les archives sont momentanément silencieuses.' });
}

function onMandataryApplicationUpdated(payload: any) {
  if (!payload?.profile || !user.value || accessState.value !== 'ready') return;
  profile.value = payload.profile;
  flash(payload.profile.status === 'active'
    ? 'Votre candidature est approuvée. Le Bureau est maintenant ouvert.'
    : 'L’Intendance a rendu sa décision sur votre candidature.', payload.profile.status === 'active' ? 'success' : 'error');
}

onMounted(() => {
  unsubscribeSessionChanges = subscribeSessionChanges(onSessionStorageChanged);
  socket.on('sessionReplaced', pauseForSessionChange);
  socket.on('mandataryApplicationUpdated', onMandataryApplicationUpdated);
  socket.on('connect', onConnected);
  socket.on('disconnect', onDisconnected);
  socket.on('connect_error', onConnectionError);
  if (socket.connected) void onConnected();
  else socket.connect();
});
onUnmounted(() => {
  sessionRevision += 1;
  configRevision += 1;
  unsubscribeSessionChanges?.();
  socket.off('sessionReplaced', pauseForSessionChange);
  socket.off('connect', onConnected);
  socket.off('disconnect', onDisconnected);
  socket.off('connect_error', onConnectionError);
  socket.off('mandataryApplicationUpdated', onMandataryApplicationUpdated);
  socket.disconnect();
  stopScanner();
  clearTurnstile();
});
</script>

<style scoped>
.moderation-hold{display:grid;gap:8px;margin-top:14px;padding:14px;border:2px solid #3b82f6;border-radius:8px;background:#dbeafe;color:#243b64}.moderation-hold[data-severity="high"]{border-color:#991b1b;background:#fee2e2;color:#681b1b}.moderation-hold>span{font:700 .75rem Georgia,serif;text-transform:uppercase;letter-spacing:.06em}.moderation-hold p{margin:0}
</style>
